<template>
  <div class="kanban-container">
    <div class="kanban-header">
      <h2>Kanban</h2>
      <button class="kanban-refresh-btn" @click="reload">↺</button>

      <!-- Selector de tablero -->
      <div class="board-selector" v-click-outside="closeBoardDropdown">
        <button class="board-selector-btn" @click="toggleBoardDropdown">
          <span class="board-selector-name">{{ activeBoard ? activeBoard.name : 'Seleccionar tablero' }}</span>
          <span v-if="activeBoard && activeBoard.user_id" class="board-personal-badge">Solo yo</span>
          <span class="board-selector-arrow">▾</span>
        </button>
        <div v-if="boardDropdownOpen" class="board-dropdown">
          <!-- Compartidos -->
          <div class="board-dropdown-section" v-if="sharedBoards.length">
            <div class="board-dropdown-label">Compartidos</div>
            <button
              v-for="b in sharedBoards"
              :key="b.id"
              class="board-dropdown-item"
              :class="{ 'board-dropdown-item--active': b.id === boardId }"
              @click="selectBoard(b.id)"
            >
              <span class="board-dropdown-dot board-dropdown-dot--shared" />
              {{ b.name }}
              <span v-if="b.description" class="board-dropdown-desc">{{ b.description }}</span>
            </button>
          </div>
          <!-- Mis tableros -->
          <div class="board-dropdown-section" v-if="personalBoards.length">
            <div class="board-dropdown-label">Mis tableros</div>
            <button
              v-for="b in personalBoards"
              :key="b.id"
              class="board-dropdown-item"
              :class="{ 'board-dropdown-item--active': b.id === boardId }"
              @click="selectBoard(b.id)"
            >
              <span class="board-dropdown-dot board-dropdown-dot--personal" />
              {{ b.name }}
              <span v-if="b.description" class="board-dropdown-desc">{{ b.description }}</span>
            </button>
          </div>
          <div v-if="!boards.length" class="board-dropdown-empty">Sin tableros</div>
          <div class="board-dropdown-divider" />
          <button class="board-dropdown-new" @click="openBoardModal">+ Nuevo tablero</button>
        </div>
      </div>

      <button v-if="boardId" class="kanban-add-col-btn" @click="openNewColumnModal">+ Columna</button>
      <button v-if="boardId" class="kanban-history-btn" @click="openBoardAudit" title="Ver historial del tablero">⏱ Historial</button>
      <button v-if="boardId" class="kanban-delete-board-btn" @click="confirmDeleteBoard" title="Eliminar tablero">✕ Eliminar</button>

      <!-- Buscador -->
      <div v-if="boardId" class="kanban-search-wrap">
        <span class="kanban-search-icon">🔍</span>
        <input
          v-model="searchQuery"
          class="kanban-search-input"
          placeholder="Buscar por nombre, teléfono, etiqueta..."
          @input="onSearch"
        />
        <button v-if="searchQuery" class="kanban-search-clear" @click="searchQuery = ''">✕</button>
      </div>
    </div>

    <!-- Filter dropdown button -->
    <div v-if="boardId" class="kanban-filter-btn-wrap" v-click-outside="closeFilterDropdown">
      <button
        class="kanban-filter-btn"
        :class="{ 'kanban-filter-btn--active': hasActiveFilters }"
        @click="filterDropdownOpen = !filterDropdownOpen"
      >
        <span class="i-lucide-sliders-horizontal" style="width:14px;height:14px;display:inline-block;vertical-align:middle;margin-right:4px;" />
        Filtros
        <span v-if="hasActiveFilters" class="kanban-filter-badge">{{ filterPriorities.length + filterLabels.length }}</span>
        <span class="i-lucide-chevron-down" style="width:12px;height:12px;display:inline-block;vertical-align:middle;margin-left:4px;opacity:0.6;" />
      </button>
      <div v-if="filterDropdownOpen" class="kanban-filter-dropdown">
        <div class="kfd-section">
          <div class="kfd-label">Prioridad</div>
          <div class="kfd-options">
            <label v-for="p in [{v:'urgent',emoji:'🔴',t:'Urgente'},{v:'high',emoji:'🟠',t:'Alta'},{v:'medium',emoji:'🟡',t:'Media'},{v:'low',emoji:'🔵',t:'Baja'}]" :key="p.v" class="kfd-option">
              <input type="checkbox" :checked="filterPriorities.includes(p.v)" @change="toggleFilterPriority(p.v)" />
              <span>{{ p.emoji }} {{ p.t }}</span>
            </label>
          </div>
        </div>
        <div v-if="availableLabels.length" class="kfd-section">
          <div class="kfd-label">Etiquetas</div>
          <div class="kfd-options">
            <label v-for="lbl in availableLabels" :key="lbl.id" class="kfd-option">
              <input type="checkbox" :checked="filterLabels.includes(lbl.title)" @change="toggleFilterLabel(lbl.title)" />
              <span :style="lbl.color ? `color:${lbl.color}` : ''">{{ lbl.title }}</span>
            </label>
          </div>
        </div>
        <div v-if="hasActiveFilters" class="kfd-footer">
          <button class="kfd-clear" @click="clearFilters; filterDropdownOpen = false">✕ Limpiar filtros</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="kanban-empty"><p>Cargando tablero...</p></div>
    <div v-else-if="!boardId" class="kanban-empty">
      <p>Selecciona o crea un tablero para comenzar.</p>
    </div>
    <div v-else-if="columns.length === 0" class="kanban-empty kanban-empty--full">
      <div class="kanban-empty-icon">⬛</div>
      <p class="kanban-empty-title">Este tablero está vacío</p>
      <p class="kanban-empty-sub">Crea la primera columna para empezar a organizar tus conversaciones.</p>
      <button class="kanban-empty-btn" @click="openNewColumnModal">+ Agregar primera columna</button>
    </div>
    <div v-else class="kanban-board">
      <div
        v-for="column in columns"
        :key="column.id"
        class="kanban-column"
        :style="{ background: colBg(column.color) }"
        @dragover.prevent
        @drop="onDrop($event, column)"
      >
        <div class="column-header" :style="{ borderTopColor: column.color }">
          <span class="column-name">
            {{ column.name }}
            <span v-if="column.label_filter" class="col-tag-badge">🏷 {{ column.label_filter }}</span>
            <span v-if="column.user_id" class="col-tag-badge col-tag-badge--personal">🔒 Solo yo</span>
          </span>
          <div class="col-header-actions">
            <span v-if="column.is_inbox_column" class="col-inbox-badge" title="Columna de llegada">📥</span>
            <button
              v-if="column.is_inbox_column"
              class="col-sync-btn"
              title="Sincronizar conversaciones nuevas"
              @click.stop="syncInboxColumn(column)"
            >↻</button>
            <button class="col-move-btn" title="Mover izquierda" @click.stop="moveColumn(column, -1)">‹</button>
            <button class="col-move-btn" title="Mover derecha" @click.stop="moveColumn(column, 1)">›</button>
            <span class="column-count">{{ visibleCount(column) }}</span>
            <button class="col-delete-btn" title="Eliminar columna" @click="confirmDeleteColumn(column)">✕</button>
          </div>
        </div>
        <div class="column-cards">
          <div
            v-for="card in column.kanban_cards"
            v-show="isCardVisible(card)"
            :key="card.id"
            class="kanban-card"
            :class="{
              'kanban-card--sla': slaExceeded(card, column),
              'kanban-card--drop-before': dropIndicator.cardId === card.id && dropIndicator.before,
              'kanban-card--drop-after': dropIndicator.cardId === card.id && !dropIndicator.before,
            }"
            draggable="true"
            @dragstart="onDragStart($event, card, column)"
            @dragend="onDragEnd"
            @dragover.prevent.stop="onCardDragOver($event, card)"
            @dragleave="onCardDragLeave(card)"
            @drop.stop="onDropOnCard($event, card, column)"
          >
            <template v-if="card.conversation">
              <!-- Acciones rápidas -->
              <div class="card-actions">
                <button
                  class="card-action-btn card-action-btn--go"
                  title="Ir a conversación"
                  @click.stop="openConversation(card.conversation_id)"
                >→</button>
                <button
                  class="card-action-btn card-action-btn--audit"
                  title="Ver auditoría"
                  @click.stop="openAudit(card, column)"
                >⏱</button>
                <button
                  class="card-action-btn card-action-btn--remove"
                  title="Quitar del Kanban"
                  @click.stop="removeCard(card, column)"
                >✕</button>
              </div>

              <div class="card-header">
                <div class="card-contact">
                  <div class="card-contact-name-row">
                    <span
                      class="card-contact-name"
                      :class="{ 'card-contact-name--unread': card.conversation.has_unread }"
                    >{{ card.conversation.contact_name || 'Sin nombre' }}</span>
                    <span
                      v-if="card.conversation.has_unread"
                      class="card-unread-badge"
                      :title="`${card.conversation.unread_count} mensaje(s) sin leer`"
                    >{{ card.conversation.unread_count > 9 ? '9+' : card.conversation.unread_count }}</span>
                  </div>
                  <span v-if="card.conversation.contact_phone" class="card-contact-phone">{{ card.conversation.contact_phone }}</span>
                </div>
                <!-- Agente (editable inline) -->
                <div class="card-agent-wrap" @click.stop>
                  <div
                    v-if="card.conversation.assignee_name"
                    class="card-assignee-pill card-assignee-pill--clickable"
                    title="Cambiar agente"
                    @click.stop="openAgentMenu(card)"
                  >
                    <span class="card-assignee-initials">{{ initials(card.conversation.assignee_name) }}</span>
                    <span class="card-assignee-fullname">{{ card.conversation.assignee_name }}</span>
                  </div>
                  <button
                    v-else
                    class="agent-add-btn"
                    title="Asignar agente"
                    @click.stop="openAgentMenu(card)"
                  >+ Agente</button>
                  <div v-if="agentMenu.cardId === card.id" class="card-inline-menu card-inline-menu--agent" @click.stop>
                    <div v-if="agentsLoading" class="card-inline-menu-item">Cargando agentes...</div>
                    <template v-else>
                      <div
                        class="card-inline-menu-item"
                        :class="{ 'card-inline-menu-item--active': !card.conversation.assignee_name }"
                        @click.stop="setAgent(card, null)"
                      >— Sin asignar</div>
                      <div
                        v-for="agent in availableAgents"
                        :key="agent.id"
                        class="card-inline-menu-item"
                        :class="{ 'card-inline-menu-item--active': card.conversation.assignee_name === agent.name }"
                        @click.stop="setAgent(card, agent)"
                      >
                        <span class="agent-menu-initials">{{ initials(agent.name) }}</span>
                        {{ agent.name }}
                        <span v-if="card.conversation.assignee_name === agent.name" class="label-menu-check">✓</span>
                      </div>
                      <div v-if="!availableAgents.length" class="card-inline-menu-item" style="color:#9ca3af;">Sin agentes</div>
                    </template>
                  </div>
                </div>
              </div>

              <p
                v-if="card.conversation.last_message"
                class="card-last-message"
                :class="{ 'card-last-message--unread': card.conversation.has_unread }"
              >
                {{ card.conversation.last_message }}
              </p>


              <div class="card-footer">
                <!-- Inline priority editor -->
                <div class="card-priority-wrap" @click.stop>
                  <span
                    v-if="card.conversation.priority"
                    class="priority-badge"
                    :class="`priority-badge--${card.conversation.priority}`"
                    @click.stop="openPriorityMenu(card)"
                  >{{ priorityLabel(card.conversation.priority) }}</span>
                  <button
                    v-else
                    class="priority-add-btn"
                    @click.stop="openPriorityMenu(card)"
                  >+ Prioridad</button>
                  <div v-if="priorityMenu.cardId === card.id" class="card-inline-menu" @click.stop>
                    <div
                      class="card-inline-menu-item"
                      :class="{ 'card-inline-menu-item--active': !card.conversation.priority }"
                      @click.stop="setPriority(card, null)"
                    >— Sin prioridad</div>
                    <div
                      class="card-inline-menu-item"
                      :class="{ 'card-inline-menu-item--active': card.conversation.priority === 'urgent' }"
                      @click.stop="setPriority(card, 'urgent')"
                    >🔴 Urgente</div>
                    <div
                      class="card-inline-menu-item"
                      :class="{ 'card-inline-menu-item--active': card.conversation.priority === 'high' }"
                      @click.stop="setPriority(card, 'high')"
                    >🟠 Alta</div>
                    <div
                      class="card-inline-menu-item"
                      :class="{ 'card-inline-menu-item--active': card.conversation.priority === 'medium' }"
                      @click.stop="setPriority(card, 'medium')"
                    >🟡 Media</div>
                    <div
                      class="card-inline-menu-item"
                      :class="{ 'card-inline-menu-item--active': card.conversation.priority === 'low' }"
                      @click.stop="setPriority(card, 'low')"
                    >🔵 Baja</div>
                  </div>
                </div>

                <span
                  v-if="card.conversation.waiting_minutes != null"
                  :class="waitingClass(card.conversation.waiting_minutes)"
                >{{ formatWaiting(card.conversation.waiting_minutes) }}</span>

                <span
                  v-if="slaExceeded(card, column)"
                  class="sla-badge"
                  :title="`Lleva ${formatWaiting(card.minutes_in_column)} en esta columna (límite: ${formatWaiting(column.sla_minutes)})`"
                >⚠️ {{ formatWaiting(card.minutes_in_column) }}</span>

                <!-- Labels with inline label editor -->
                <div class="card-labels" @click.stop>
                  <span
                    v-for="label in (card.conversation.labels || [])"
                    :key="label"
                    class="card-label"
                    :style="labelStyle(label)"
                  >{{ label }}</span>
                  <div class="card-label-wrap" @click.stop>
                    <button class="card-label-add-btn" @click.stop="openLabelMenu(card)">+ etiqueta</button>
                    <div v-if="labelMenu.cardId === card.id" class="label-menu" @click.stop>
                      <div
                        v-for="lbl in availableLabels"
                        :key="lbl.id"
                        class="label-menu-item"
                        @click.stop="toggleLabel(card, lbl.title)"
                      >
                        <span
                          class="label-menu-dot"
                          :style="{ background: labelColors[lbl.title] || '#6366f1' }"
                        />
                        {{ lbl.title }}
                        <span
                          v-if="(card.conversation.labels || []).includes(lbl.title)"
                          class="label-menu-check"
                        >✓</span>
                      </div>
                      <div v-if="!availableLabels.length" style="padding:8px 12px;font-size:0.75rem;color:#9ca3af;">Sin etiquetas</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <p class="card-conv-fallback">Conversación #{{ card.conversation_id }}</p>
            </template>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Modal nuevo tablero -->
  <div v-if="boardModal.open" class="audit-overlay" @click.self="boardModal.open = false">
    <div class="audit-modal col-modal">
      <div class="audit-modal-header">
        <span>Nuevo tablero</span>
        <button class="audit-close" @click="boardModal.open = false">✕</button>
      </div>
      <div class="col-modal-body">
        <label class="col-modal-label">Nombre</label>
        <input v-model="boardModal.name" class="col-modal-input" placeholder="Ej: Ventas Q3" maxlength="60" />

        <label class="col-modal-label">Descripción (opcional)</label>
        <input v-model="boardModal.description" class="col-modal-input" placeholder="Ej: Seguimiento de prospectos..." />

        <label class="col-modal-label">Visibilidad</label>
        <div class="col-modal-radios">
          <label><input type="radio" v-model="boardModal.visibility" value="shared" /> Compartido (todos)</label>
          <label><input type="radio" v-model="boardModal.visibility" value="personal" /> Solo yo</label>
        </div>

        <label class="col-modal-checkbox-wrap">
          <input type="checkbox" v-model="boardModal.isDefault" />
          <span>
            <strong>Tablero predeterminado</strong>
            <span class="col-modal-hint" style="display:block;margin-top:1px;">Se abrirá automáticamente al entrar al Kanban</span>
          </span>
        </label>

        <div v-if="boardModal.error" class="col-modal-error">{{ boardModal.error }}</div>
        <button class="col-modal-save" :disabled="boardModal.saving" @click="saveNewBoard">
          {{ boardModal.saving ? 'Creando...' : 'Crear tablero' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal confirmar eliminar columna -->
  <div v-if="deleteColModal.open" class="audit-overlay" @click.self="deleteColModal.open = false">
    <div class="audit-modal confirm-modal">
      <div class="confirm-modal-icon">🗑️</div>
      <h3 class="confirm-modal-title">Eliminar columna</h3>
      <p class="confirm-modal-msg">
        ¿Estás seguro de que deseas eliminar la columna
        <strong>"{{ deleteColModal.columnName }}"</strong>?
        <br>Todas las tarjetas dentro serán eliminadas permanentemente.
      </p>
      <div class="confirm-modal-actions">
        <button class="confirm-btn confirm-btn--cancel" @click="deleteColModal.open = false">Cancelar</button>
        <button class="confirm-btn confirm-btn--danger" :disabled="deleteColModal.deleting" @click="doDeleteColumn">
          {{ deleteColModal.deleting ? 'Eliminando...' : 'Sí, eliminar' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal nueva columna -->
  <div v-if="colModal.open" class="audit-overlay" @click.self="colModal.open = false">
    <div class="audit-modal col-modal">
      <div class="audit-modal-header">
        <span>Nueva columna</span>
        <button class="audit-close" @click="colModal.open = false">✕</button>
      </div>
      <div class="col-modal-body">
        <label class="col-modal-label">Nombre</label>
        <input v-model="colModal.name" class="col-modal-input" placeholder="Ej: En proceso" maxlength="40" />

        <label class="col-modal-label">Color</label>
        <div class="col-color-swatches">
          <button
            v-for="c in colorSwatches"
            :key="c"
            class="col-swatch"
            :style="{ background: c, outline: colModal.color === c ? '3px solid #6366f1' : 'none' }"
            @click="colModal.color = c"
          />
        </div>

        <label class="col-modal-label">Tiempo máximo en columna — opcional</label>
        <div class="col-sla-row">
          <input
            v-model.number="colModal.slaValue"
            type="number"
            min="0"
            class="col-modal-input col-sla-input"
            placeholder="Sin límite"
          />
          <select v-model="colModal.slaUnit" class="col-modal-input col-sla-unit">
            <option value="minutes">minutos</option>
            <option value="hours">horas</option>
            <option value="days">días</option>
          </select>
        </div>
        <p v-if="slaToMinutes()" class="col-sla-preview">
          ⚠️ Las tarjetas se marcarán tras <strong>{{ formatWaiting(slaToMinutes()) }}</strong> en esta columna ({{ slaToMinutes().toLocaleString('es-CO') }} minutos)
        </p>
        <p v-else class="col-modal-hint">Deja vacío para no aplicar límite de tiempo.</p>

        <label class="col-modal-label">Visibilidad</label>
        <div class="col-modal-radios">
          <label>
            <input type="radio" v-model="colModal.visibility" value="shared" /> Compartida (todos)
          </label>
          <label>
            <input type="radio" v-model="colModal.visibility" value="personal" /> Solo yo
          </label>
        </div>

        <label class="col-modal-label">Tipo</label>
        <div class="col-modal-radios">
          <label><input type="radio" v-model="colModal.type" value="normal" /> Normal</label>
          <label><input type="radio" v-model="colModal.type" value="label" /> Por etiqueta</label>
          <label><input type="radio" v-model="colModal.type" value="inbox" /> Llegada (nuevas)</label>
        </div>
        <p v-if="colModal.type === 'inbox'" class="col-modal-hint">
          Se agregarán automáticamente todas las conversaciones nuevas. Solo puede haber una por tablero.
        </p>

        <div v-if="colModal.type === 'label'" class="col-modal-label-wrap">
          <label class="col-modal-label">Etiqueta</label>
          <select v-if="availableLabels.length" v-model="colModal.labelFilter" class="col-modal-input">
            <option value="">— Selecciona una etiqueta —</option>
            <option v-for="lbl in availableLabels" :key="lbl.id" :value="lbl.title">
              {{ lbl.title }}
            </option>
          </select>
          <input v-else v-model="colModal.labelFilter" class="col-modal-input" placeholder="Ej: cotizacion" />
          <p class="col-modal-hint">Se agregarán automáticamente todas las conversaciones con esa etiqueta.</p>
        </div>

        <div v-if="colModal.error" class="col-modal-error">{{ colModal.error }}</div>
        <button class="col-modal-save" :disabled="colModal.saving" @click="saveNewColumn">
          {{ colModal.saving ? 'Creando...' : 'Crear columna' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal confirmar eliminar TABLERO -->
  <div v-if="deleteBoardModal.open" class="audit-overlay" @click.self="deleteBoardModal.open = false">
    <div class="audit-modal confirm-modal">
      <div class="confirm-modal-icon">🗑️</div>
      <h3 class="confirm-modal-title">Eliminar tablero</h3>
      <p class="confirm-modal-msg">
        ¿Estás seguro de que deseas eliminar el tablero
        <strong>"{{ deleteBoardModal.boardName }}"</strong>?
        <br>Se eliminarán todas sus columnas y tarjetas de forma permanente.
      </p>
      <div class="confirm-modal-actions">
        <button class="confirm-btn confirm-btn--cancel" @click="deleteBoardModal.open = false">Cancelar</button>
        <button class="confirm-btn confirm-btn--danger" :disabled="deleteBoardModal.deleting" @click="doDeleteBoard">
          {{ deleteBoardModal.deleting ? 'Eliminando...' : 'Sí, eliminar tablero' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal auditoría del TABLERO -->
  <div v-if="boardAuditModal.open" class="audit-overlay" @click.self="boardAuditModal.open = false">
    <div class="audit-modal audit-modal--wide">
      <div class="audit-modal-header">
        <span>Historial — {{ boardAuditModal.boardName }}</span>
        <button class="audit-close" @click="boardAuditModal.open = false">✕</button>
      </div>
      <div v-if="boardAuditModal.loading" class="audit-loading">Cargando historial...</div>
      <div v-else-if="boardAuditModal.logs.length === 0" class="audit-empty">Sin eventos registrados aún.</div>
      <div v-else class="audit-timeline">
        <div v-for="log in boardAuditModal.logs" :key="log.id" class="audit-entry">
          <div class="audit-dot" :class="auditDotClass(log.action)" />
          <div class="audit-body">
            <div class="audit-action">
              <span class="audit-action-label" :class="auditDotClass(log.action)">
                {{ auditActionLabel(log.action) }}
              </span>
              <span v-if="log.contact_name" class="audit-contact-name">— {{ log.contact_name }}</span>
              <span v-if="log.from_column && log.to_column" class="audit-columns">
                <span class="audit-col-from">{{ log.from_column }}</span>
                <span class="audit-arrow">→</span>
                <span class="audit-col-to" :style="{ color: log.to_column_color }">{{ log.to_column }}</span>
              </span>
              <span v-else-if="log.to_column && !log.from_column" class="audit-col-to" :style="{ color: log.to_column_color }">
                {{ log.to_column }}
              </span>
              <span v-else-if="log.from_column && !log.to_column" class="audit-col-from">
                {{ log.from_column }}
              </span>
              <span v-else-if="log.metadata && (log.metadata.column_name || log.metadata.board_name)" class="audit-col-from">
                {{ log.metadata.column_name || log.metadata.board_name }}
              </span>
            </div>
            <div class="audit-meta">
              <span class="audit-user">{{ log.user_name || 'Sistema' }}</span>
              <span class="audit-time">{{ formatDate(log.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal auditoría de TARJETA -->
  <div v-if="auditModal.open" class="audit-overlay" @click.self="auditModal.open = false">
    <div class="audit-modal">
      <div class="audit-modal-header">
        <span>Auditoría — {{ auditModal.contactName }}</span>
        <button class="audit-close" @click="auditModal.open = false">✕</button>
      </div>
      <div v-if="auditModal.loading" class="audit-loading">Cargando...</div>
      <div v-else-if="auditModal.logs.length === 0" class="audit-empty">
        Sin movimientos registrados aún.
      </div>
      <div v-else class="audit-timeline">
        <div v-for="log in auditModal.logs" :key="log.id" class="audit-entry">
          <div class="audit-dot" :class="auditDotClass(log.action)" />
          <div class="audit-body">
            <div class="audit-action">
              <span class="audit-action-label" :class="auditDotClass(log.action)">
                {{ auditActionLabel(log.action) }}
              </span>
              <span v-if="log.from_column && log.to_column" class="audit-columns">
                <span class="audit-col-from">{{ log.from_column }}</span>
                <span class="audit-arrow">→</span>
                <span class="audit-col-to" :style="{ color: log.to_column_color }">{{ log.to_column }}</span>
              </span>
              <span v-else-if="log.to_column" class="audit-col-to" :style="{ color: log.to_column_color }">
                {{ log.to_column }}
              </span>
            </div>
            <div class="audit-meta">
              <span class="audit-user">{{ log.user_name || 'Sistema' }}</span>
              <span class="audit-time">{{ formatDate(log.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'KanbanView',
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) { document.removeEventListener('click', el._clickOutside); },
    },
  },
  computed: {
    activeBoard() { return this.boards.find(b => b.id === this.boardId) || null; },
    sharedBoards() { return this.boards.filter(b => !b.user_id); },
    personalBoards() { return this.boards.filter(b => b.user_id); },
    hasActiveFilters() {
      return this.filterPriorities.length > 0 || this.filterLabels.length > 0;
    },
  },
  data() {
    return {
      boards: [],
      columns: [],
      boardId: null,
      loading: true,
      token: null,
      dragCard: null,
      dragSourceColumn: null,
      wasDragging: false,
      dropIndicator: { cardId: null, before: false },
      boardModal: { open: false, name: '', description: '', visibility: 'shared', isDefault: false, saving: false, error: '' },
      boardDropdownOpen: false,
      auditModal: { open: false, loading: false, logs: [], contactName: '', cardId: null, columnId: null },
      colModal: { open: false, name: '', color: '#378add', visibility: 'shared', type: 'normal', labelFilter: '', slaValue: null, slaUnit: 'hours', saving: false, error: '' },
      deleteColModal: { open: false, columnId: null, columnName: '', deleting: false },
      deleteBoardModal: { open: false, boardName: '', deleting: false },
      boardAuditModal: { open: false, loading: false, logs: [], boardName: '' },
      searchQuery: '',
      labelColors: {},
      colorSwatches: ['#888888','#378add','#ef9f27','#1d9e75','#639922','#e45454','#9333ea','#ec4899','#0ea5e9','#f59e0b'],
      availableLabels: [],
      filterPriorities: [],
      filterLabels: [],
      filterDropdownOpen: false,
      priorityMenu: { cardId: null },
      labelMenu: { cardId: null },
      agentMenu: { cardId: null },
      availableAgents: [],
      agentsLoading: false,
      currentUserId: null,
      realtimeReloadTimer: null,
    };
  },
  mounted() {
    this.waitForToken();
    document.addEventListener('click', this.closeMenus);
    emitter.on('kanban.updated', this.onRealtimeKanbanUpdate);
  },
  unmounted() {
    document.removeEventListener('click', this.closeMenus);
    emitter.off('kanban.updated', this.onRealtimeKanbanUpdate);
    clearTimeout(this.realtimeReloadTimer);
  },
  methods: {
    getToken() {
      try {
        const app = document.querySelector('#app').__vue_app__;
        const currentUser = app.config.globalProperties.$store.state.auth.currentUser;
        this.currentUserId = currentUser?.id || null;
        return currentUser?.access_token || null;
      } catch (e) { return null; }
    },
    // Tiempo real: otros agentes moviendo/creando/eliminando tarjetas o columnas
    onRealtimeKanbanUpdate(data) {
      if (!data) return;
      // Cambios propios: el estado local ya está actualizado de forma optimista
      if (this.currentUserId && data.actor_id === this.currentUserId) return;

      const accountId = this.$route.params.accountId;
      if (data.action === 'board_created' || data.action === 'board_deleted') {
        this.loadBoard();
        return;
      }
      if (data.board_id !== this.boardId) return;

      // Pequeño debounce: varios eventos casi simultáneos (ej. reordenar) disparan un solo reload
      clearTimeout(this.realtimeReloadTimer);
      this.realtimeReloadTimer = setTimeout(() => {
        this.loadColumns(accountId);
      }, 300);
    },
    waitForToken() {
      const t = this.getToken();
      if (t) { this.token = t; this.loadBoard(); return; }
      let attempts = 0;
      const interval = setInterval(() => {
        const tok = this.getToken();
        if (tok) { clearInterval(interval); this.token = tok; this.loadBoard(); }
        else if (++attempts > 20) { clearInterval(interval); this.loading = false; }
      }, 500);
    },
    async loadBoard() {
      try {
        this.loading = true;
        const accountId = this.$route.params.accountId;
        const res = await fetch(`/api/v1/accounts/${accountId}/kanban_boards`, {
          headers: { 'api_access_token': this.token },
        });
        const boards = await res.json();
        this.boards = Array.isArray(boards) ? boards : [];
        if (this.boards.length > 0) {
          const def = this.boards.find(b => b.is_default);
          this.boardId = (def || this.boards[0]).id;
          await this.loadColumns(accountId);
        }
      } catch (e) {
        console.error('Kanban error:', e);
      } finally {
        this.loading = false;
      }
    },
    async loadColumns(accountId) {
      const res = await fetch(`/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}`, {
        headers: { 'api_access_token': this.token },
      });
      const board = await res.json();
      this.labelColors = board.label_colors || {};
      this.columns = board.kanban_columns || [];
      // Load labels if not already loaded
      if (this.availableLabels.length === 0) {
        try {
          const lr = await fetch(`/api/v1/accounts/${accountId}/labels`, { headers: { 'api_access_token': this.token } });
          const ld = await lr.json();
          this.availableLabels = ld.payload || [];
        } catch (e) { /* ignore */ }
      }
    },
    async switchBoard(id) {
      if (id === this.boardId) return;
      this.boardId = id;
      this.loading = true;
      try { await this.loadColumns(this.$route.params.accountId); }
      finally { this.loading = false; }
    },
    reload() {
      this.loadColumns(this.$route.params.accountId);
    },
    toggleBoardDropdown() { this.boardDropdownOpen = !this.boardDropdownOpen; },
    closeBoardDropdown() { this.boardDropdownOpen = false; },
    async selectBoard(id) {
      this.boardDropdownOpen = false;
      await this.switchBoard(id);
    },
    openBoardModal() {
      this.boardDropdownOpen = false;
      this.boardModal = { open: true, name: '', description: '', visibility: 'shared', isDefault: false, saving: false, error: '' };
    },
    async saveNewBoard() {
      if (!this.boardModal.name.trim()) { this.boardModal.error = 'El nombre es obligatorio.'; return; }
      this.boardModal.saving = true;
      this.boardModal.error = '';
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(`/api/v1/accounts/${accountId}/kanban_boards`, {
          method: 'POST',
          headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kanban_board: {
            name: this.boardModal.name.trim(),
            description: this.boardModal.description.trim(),
            visibility: this.boardModal.visibility,
            is_default: this.boardModal.isDefault,
            no_defaults: true,
          }}),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.errors?.join(', ') || 'Error'); }
        const board = await res.json();
        this.boards.push(board);
        this.boardModal.open = false;
        await this.switchBoard(board.id);
      } catch (e) {
        this.boardModal.error = e.message;
      } finally {
        this.boardModal.saving = false;
      }
    },
    async openAudit(card, column) {
      const name = card.conversation?.contact_name || `#${card.conversation_id}`;
      this.auditModal = { open: true, loading: true, logs: [], contactName: name, cardId: card.id, columnId: column.id };
      try {
        const accountId = this.$route.params.accountId;
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns/${column.id}/kanban_cards/${card.id}/kanban_card_logs`,
          { headers: { 'api_access_token': this.token } }
        );
        this.auditModal.logs = await res.json();
      } catch (e) {
        this.auditModal.logs = [];
      } finally {
        this.auditModal.loading = false;
      }
    },
    auditActionLabel(action) {
      return {
        created: 'Tarjeta agregada', moved: 'Tarjeta movida', removed: 'Tarjeta quitada',
        board_created: 'Tablero creado', board_deleted: 'Tablero eliminado',
        column_created: 'Columna creada', column_deleted: 'Columna eliminada',
      }[action] || action;
    },
    auditDotClass(action) {
      if (['created', 'board_created', 'column_created'].includes(action)) return 'dot--created';
      if (['removed', 'board_deleted', 'column_deleted'].includes(action)) return 'dot--removed';
      return 'dot--moved';
    },
    confirmDeleteBoard() {
      const board = this.activeBoard;
      if (!board) return;
      this.deleteBoardModal = { open: true, boardName: board.name, deleting: false };
    },
    async doDeleteBoard() {
      this.deleteBoardModal.deleting = true;
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}`,
          { method: 'DELETE', headers: { 'api_access_token': this.token } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.boards = this.boards.filter(b => b.id !== this.boardId);
        this.deleteBoardModal.open = false;
        // Switch to another board or clear
        if (this.boards.length > 0) {
          const next = this.boards.find(b => b.is_default) || this.boards[0];
          await this.switchBoard(next.id);
        } else {
          this.boardId = null;
          this.columns = [];
        }
      } catch (e) {
        console.error('Error eliminando tablero:', e);
        this.deleteBoardModal.deleting = false;
      }
    },
    async openBoardAudit() {
      const board = this.activeBoard;
      this.boardAuditModal = { open: true, loading: true, logs: [], boardName: board?.name || '' };
      try {
        const accountId = this.$route.params.accountId;
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/audit`,
          { headers: { 'api_access_token': this.token } }
        );
        this.boardAuditModal.logs = await res.json();
      } catch (e) {
        this.boardAuditModal.logs = [];
      } finally {
        this.boardAuditModal.loading = false;
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    async removeCard(card, column) {
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns/${column.id}/kanban_cards/${card.id}`,
          { method: 'DELETE', headers: { 'api_access_token': this.token } }
        );
        if (!res.ok) {
          console.error('[KANBAN] Error quitando tarjeta, HTTP', res.status);
          useAlert('No se pudo quitar la tarjeta del Kanban. Intenta de nuevo.');
          return;
        }
        column.kanban_cards = column.kanban_cards.filter(c => c.id !== card.id);
      } catch (e) {
        console.error('Error removing card:', e);
        useAlert('No se pudo quitar la tarjeta del Kanban. Intenta de nuevo.');
      }
    },
    openConversation(conversationId) {
      if (this.wasDragging) return;
      this.$router.push({
        name: 'inbox_conversation',
        params: { accountId: this.$route.params.accountId, conversation_id: conversationId },
      });
    },
    // Filtros (sin clonar columnas: solo ocultan/muestran tarjetas)
    isCardVisible(card) {
      const conv = card.conversation;
      if (!conv) return false;
      const q = this.searchQuery.trim().toLowerCase();
      const fp = this.filterPriorities;
      const fl = this.filterLabels;
      if (fp.length > 0 && !fp.includes(conv.priority)) return false;
      if (fl.length > 0) {
        const cardLabels = conv.labels || [];
        if (!fl.some(l => cardLabels.includes(l))) return false;
      }
      if (q) {
        if ((conv.contact_name || '').toLowerCase().includes(q)) return true;
        if ((conv.contact_phone || '').toLowerCase().includes(q)) return true;
        if (String(conv.display_id || '').includes(q)) return true;
        if ((conv.labels || []).some(l => l.toLowerCase().includes(q))) return true;
        return false;
      }
      return true;
    },
    visibleCount(column) {
      return column.kanban_cards.filter(c => this.isCardVisible(c)).length;
    },
    // SLA
    slaToMinutes() {
      const v = this.colModal.slaValue;
      if (!v || v <= 0) return null;
      const mult = { minutes: 1, hours: 60, days: 1440 }[this.colModal.slaUnit] || 60;
      return Math.round(v * mult);
    },
    slaExceeded(card, column) {
      return !!(column.sla_minutes && card.minutes_in_column != null && card.minutes_in_column > column.sla_minutes);
    },
    // Drag & drop
    onDragStart(event, card, column) {
      this.dragCard = card;
      this.dragSourceColumn = column;
      this.wasDragging = false;
      event.dataTransfer.effectAllowed = 'move';
    },
    onDragEnd() {
      this.wasDragging = true;
      this.dropIndicator = { cardId: null, before: false };
      setTimeout(() => { this.dragCard = null; this.dragSourceColumn = null; this.wasDragging = false; }, 200);
    },
    onCardDragOver(event, card) {
      if (!this.dragCard || card.id === this.dragCard.id) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const before = event.clientY < rect.top + rect.height / 2;
      if (this.dropIndicator.cardId !== card.id || this.dropIndicator.before !== before) {
        this.dropIndicator = { cardId: card.id, before };
      }
    },
    onCardDragLeave(card) {
      if (this.dropIndicator.cardId === card.id) {
        this.dropIndicator = { cardId: null, before: false };
      }
    },
    async onDropOnCard(event, targetCard, targetColumn) {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      const indicator = this.dropIndicator;
      this.dropIndicator = { cardId: null, before: false };
      if (!this.dragCard || this.dragCard.id === targetCard.id) return;
      let idx = targetColumn.kanban_cards.findIndex(c => c.id === targetCard.id);
      if (idx === -1) return;
      if (!indicator.before) idx += 1;
      await this.moveCardTo(targetColumn, idx);
    },
    async onDrop(event, targetColumn) {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      this.dropIndicator = { cardId: null, before: false };
      if (!this.dragCard || !this.dragSourceColumn || !targetColumn) return;
      // Soltar en el fondo de la columna = al final
      await this.moveCardTo(targetColumn, targetColumn.kanban_cards.length);
    },
    async moveCardTo(targetColumn, insertIndex) {
      const card = this.dragCard;
      const sourceColumn = this.dragSourceColumn;
      if (!card || !sourceColumn || !targetColumn) return;

      const sameColumn = sourceColumn.id === targetColumn.id;
      const srcIdx = sourceColumn.kanban_cards.findIndex(c => c.id === card.id);
      if (srcIdx === -1) return;
      // Sin movimiento real (misma posición)
      if (sameColumn && (insertIndex === srcIdx || insertIndex === srcIdx + 1)) return;

      const accountId = this.$route.params.accountId;

      // Mutación optimista sobre los objetos reactivos reales
      sourceColumn.kanban_cards.splice(srcIdx, 1);
      let idx = insertIndex;
      if (sameColumn && srcIdx < insertIndex) idx -= 1;
      idx = Math.max(0, Math.min(idx, targetColumn.kanban_cards.length));
      targetColumn.kanban_cards.splice(idx, 0, card);
      card.kanban_column_id = targetColumn.id;

      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns/${sourceColumn.id}/kanban_cards/${card.id}`,
          {
            method: 'PATCH',
            headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ kanban_card: { kanban_column_id: targetColumn.id, position: idx + 1 } }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (e) {
        console.error('[KANBAN] Error moviendo tarjeta, revirtiendo:', e);
        const ti = targetColumn.kanban_cards.findIndex(c => c.id === card.id);
        if (ti !== -1) targetColumn.kanban_cards.splice(ti, 1);
        sourceColumn.kanban_cards.splice(srcIdx, 0, card);
        card.kanban_column_id = sourceColumn.id;
      }
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
    },
    formatWaiting(minutes) {
      if (minutes < 60) return `${minutes}m`;
      if (minutes < 1440) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
      }
      const d = Math.floor(minutes / 1440);
      const h = Math.floor((minutes % 1440) / 60);
      return h > 0 ? `${d}d ${h}h` : `${d}d`;
    },
    priorityLabel(p) {
      return { urgent: '🔴 Urgente', high: '🟠 Alta', medium: '🟡 Media', low: '🔵 Baja' }[p] || p;
    },
    waitingClass(minutes) {
      if (minutes >= 120) return 'waiting-badge waiting-badge--danger';
      if (minutes >= 30)  return 'waiting-badge waiting-badge--warning';
      return 'waiting-badge waiting-badge--ok';
    },
    async openNewColumnModal() {
      this.colModal = { open: true, name: '', color: '#378add', visibility: 'shared', type: 'normal', labelFilter: '', slaValue: null, slaUnit: 'hours', saving: false, error: '' };
      if (this.availableLabels.length === 0) {
        try {
          const accountId = this.$route.params.accountId;
          const res = await fetch(`/api/v1/accounts/${accountId}/labels`, { headers: { 'api_access_token': this.token } });
          const data = await res.json();
          this.availableLabels = data.payload || [];
        } catch (e) { this.availableLabels = []; }
      }
    },
    async saveNewColumn() {
      if (!this.colModal.name.trim()) { this.colModal.error = 'El nombre es obligatorio.'; return; }
      this.colModal.saving = true;
      this.colModal.error = '';
      const accountId = this.$route.params.accountId;
      try {
        const body = {
          kanban_column: {
            name: this.colModal.name.trim(),
            color: this.colModal.color,
            visibility: this.colModal.visibility,
            label_filter: this.colModal.type === 'label' ? this.colModal.labelFilter.trim() : null,
            is_inbox_column: this.colModal.type === 'inbox',
            sla_minutes: this.slaToMinutes(),
          }
        };
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns`,
          { method: 'POST', headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        );
        if (!res.ok) { const e = await res.json(); throw new Error(e.errors?.join(', ') || 'Error'); }
        const col = await res.json();
        col.kanban_cards = [];
        this.columns.push(col);
        this.colModal.open = false;
      } catch (e) {
        this.colModal.error = e.message;
      } finally {
        this.colModal.saving = false;
      }
    },
    async moveColumn(column, direction) {
      const idx = this.columns.findIndex(c => c.id === column.id);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= this.columns.length) return;

      // Swap in local state
      const cols = [...this.columns];
      [cols[idx], cols[newIdx]] = [cols[newIdx], cols[idx]];
      this.columns = cols;

      // Persist both positions
      const accountId = this.$route.params.accountId;
      const base = `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns`;
      await Promise.all([
        fetch(`${base}/${cols[idx].id}`, {
          method: 'PATCH',
          headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kanban_column: { position: idx + 1 } }),
        }),
        fetch(`${base}/${cols[newIdx].id}`, {
          method: 'PATCH',
          headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kanban_column: { position: newIdx + 1 } }),
        }),
      ]);
    },
    async syncInboxColumn(column) {
      const accountId = this.$route.params.accountId;
      try {
        await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns/${column.id}/sync_inbox`,
          { method: 'POST', headers: { 'api_access_token': this.token } }
        );
        await this.loadColumns(accountId);
      } catch (e) { console.error('Error sync inbox:', e); }
    },
    confirmDeleteColumn(column) {
      this.deleteColModal = { open: true, columnId: column.id, columnName: column.name, deleting: false };
    },
    async doDeleteColumn() {
      this.deleteColModal.deleting = true;
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/kanban_boards/${this.boardId}/kanban_columns/${this.deleteColModal.columnId}`,
          { method: 'DELETE', headers: { 'api_access_token': this.token } }
        );
        if (!res.ok) {
          useAlert('No se pudo eliminar la columna. Intenta de nuevo.');
          this.deleteColModal.deleting = false;
          return;
        }
        this.columns = this.columns.filter(c => c.id !== this.deleteColModal.columnId);
        this.deleteColModal.open = false;
      } catch (e) {
        console.error('Error eliminando columna:', e);
        useAlert('No se pudo eliminar la columna. Intenta de nuevo.');
        this.deleteColModal.deleting = false;
      }
    },
    labelStyle(name) {
      const hex = this.labelColors[name];
      if (!hex) return {};
      const r = parseInt(hex.slice(1,3),16);
      const g = parseInt(hex.slice(3,5),16);
      const b = parseInt(hex.slice(5,7),16);
      const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
      return {
        background: hex,
        color: luminance > 0.55 ? '#1f2937' : '#ffffff',
      };
    },
    colBg(hex) {
      if (!hex) return undefined;
      const r = parseInt(hex.slice(1,3),16);
      const g = parseInt(hex.slice(3,5),16);
      const b = parseInt(hex.slice(5,7),16);
      const dark = document.body.classList.contains('dark');
      return dark
        ? `rgba(${r},${g},${b},0.10)`
        : `rgba(${r},${g},${b},0.12)`;
    },
    // Filter methods
    toggleFilterPriority(p) {
      const idx = this.filterPriorities.indexOf(p);
      if (idx === -1) this.filterPriorities.push(p);
      else this.filterPriorities.splice(idx, 1);
    },
    toggleFilterLabel(l) {
      const idx = this.filterLabels.indexOf(l);
      if (idx === -1) this.filterLabels.push(l);
      else this.filterLabels.splice(idx, 1);
    },
    clearFilters() {
      this.filterPriorities = [];
      this.filterLabels = [];
    },
    // Priority menu methods
    openPriorityMenu(card) {
      if (this.priorityMenu.cardId === card.id) {
        this.priorityMenu.cardId = null;
      } else {
        this.labelMenu.cardId = null;
        this.agentMenu.cardId = null;
        this.priorityMenu.cardId = card.id;
      }
    },
    async setPriority(card, priority) {
      const accountId = this.$route.params.accountId;
      try {
        await fetch(
          `/api/v1/accounts/${accountId}/conversations/${card.conversation_id}/toggle_priority`,
          {
            method: 'POST',
            headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ priority: priority }),
          }
        );
        card.conversation.priority = priority;
      } catch (e) {
        console.error('Error setting priority:', e);
      } finally {
        this.priorityMenu.cardId = null;
      }
    },
    // Agent menu methods
    async openAgentMenu(card) {
      if (this.agentMenu.cardId === card.id) {
        this.agentMenu.cardId = null;
        return;
      }
      this.priorityMenu.cardId = null;
      this.labelMenu.cardId = null;
      this.agentMenu.cardId = card.id;
      if (this.availableAgents.length === 0) {
        this.agentsLoading = true;
        try {
          const accountId = this.$route.params.accountId;
          const res = await fetch(`/api/v1/accounts/${accountId}/agents`, { headers: { 'api_access_token': this.token } });
          const data = await res.json();
          this.availableAgents = (Array.isArray(data) ? data : []).filter(a => a.confirmed !== false);
        } catch (e) {
          this.availableAgents = [];
        } finally {
          this.agentsLoading = false;
        }
      }
    },
    async setAgent(card, agent) {
      const accountId = this.$route.params.accountId;
      try {
        const res = await fetch(
          `/api/v1/accounts/${accountId}/conversations/${card.conversation_id}/assignments`,
          {
            method: 'POST',
            headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ assignee_id: agent ? agent.id : 0 }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        card.conversation.assignee_name = agent ? agent.name : null;
      } catch (e) {
        console.error('Error asignando agente:', e);
      } finally {
        this.agentMenu.cardId = null;
      }
    },
    // Label menu methods
    openLabelMenu(card) {
      if (this.labelMenu.cardId === card.id) {
        this.labelMenu.cardId = null;
      } else {
        this.priorityMenu.cardId = null;
        this.agentMenu.cardId = null;
        this.labelMenu.cardId = card.id;
      }
    },
    async toggleLabel(card, labelTitle) {
      const currentLabels = card.conversation.labels || [];
      let newLabels;
      if (currentLabels.includes(labelTitle)) {
        newLabels = currentLabels.filter(l => l !== labelTitle);
      } else {
        newLabels = [...currentLabels, labelTitle];
      }
      const accountId = this.$route.params.accountId;
      try {
        await fetch(
          `/api/v1/accounts/${accountId}/conversations/${card.conversation_id}/labels`,
          {
            method: 'POST',
            headers: { 'api_access_token': this.token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ labels: newLabels }),
          }
        );
        card.conversation.labels = newLabels;
      } catch (e) {
        console.error('Error toggling label:', e);
      }
    },
    closeMenus() {
      this.priorityMenu.cardId = null;
      this.labelMenu.cardId = null;
      this.agentMenu.cardId = null;
    },
    closeFilterDropdown() {
      this.filterDropdownOpen = false;
    },
  },
};
</script>

<style scoped>
.kanban-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  background: var(--color-background, #f0f2f5);
  overflow: hidden;
  box-sizing: border-box;
}
.kanban-board {
  overflow-x: auto !important;
  overflow-y: hidden;
}
.kanban-board::-webkit-scrollbar { height: 6px; }
.kanban-board::-webkit-scrollbar-track { background: transparent; }
.kanban-board::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
body.dark .kanban-board::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.18); }
.kanban-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
/* Board selector dropdown */
.board-selector {
  position: relative;
}
.board-selector-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--color-background-light, #e8eaed);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--color-heading, #111827);
  transition: background 0.12s;
  white-space: nowrap;
  max-width: 220px;
}
.board-selector-btn:hover { background: rgba(99,102,241,0.08); }
.board-selector-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}
.board-selector-arrow { font-size: 0.65rem; color: #9ca3af; margin-left: 2px; }
.board-personal-badge {
  font-size: 0.62rem;
  background: rgba(107,114,128,0.15);
  color: #6b7280;
  padding: 1px 5px;
  border-radius: 4px;
  font-weight: 500;
}
.board-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 240px;
  background: var(--white, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  z-index: 200;
  overflow: hidden;
  padding: 0.4rem 0;
}
.board-dropdown-section { padding: 0.25rem 0; }
.board-dropdown-label {
  padding: 0.3rem 0.9rem 0.2rem;
  font-size: 0.67rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
}
.board-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.9rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.83rem;
  color: var(--color-heading, #111827);
  text-align: left;
  transition: background 0.1s;
  flex-wrap: wrap;
}
.board-dropdown-item:hover { background: var(--color-background-light, #f3f4f6); }
.board-dropdown-item--active { background: rgba(99,102,241,0.08); color: #6366f1; font-weight: 600; }
.board-dropdown-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.board-dropdown-dot--shared { background: #6366f1; }
.board-dropdown-dot--personal { background: #f59e0b; }
.board-dropdown-desc {
  display: block;
  width: 100%;
  font-size: 0.7rem;
  color: #9ca3af;
  padding-left: 1.25rem;
  margin-top: -2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.board-dropdown-divider { height: 1px; background: var(--color-border, #e5e7eb); margin: 0.3rem 0; }
.board-dropdown-new {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.45rem 0.9rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.83rem;
  color: #6366f1;
  font-weight: 600;
  text-align: left;
  transition: background 0.1s;
}
.board-dropdown-new:hover { background: rgba(99,102,241,0.06); }
.board-dropdown-empty { padding: 0.5rem 0.9rem; font-size: 0.8rem; color: #9ca3af; }

body.dark .board-selector-btn { background: #1e2530; border-color: #374151; color: #f3f4f6; }
body.dark .board-selector-btn:hover { background: rgba(99,102,241,0.15); }
body.dark .board-dropdown { background: #1e2530; border-color: #374151; }
body.dark .board-dropdown-item { color: #f3f4f6; }
body.dark .board-dropdown-item:hover { background: #2d3748; }
body.dark .board-dropdown-item--active { background: rgba(99,102,241,0.18); color: #a5b4fc; }
body.dark .board-dropdown-divider { background: #374151; }

/* Search */
.kanban-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--color-background-light, #e8eaed);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  padding: 0.3rem 0.65rem;
  min-width: 220px;
  max-width: 300px;
  flex: 1;
}
.kanban-search-icon { font-size: 0.8rem; flex-shrink: 0; opacity: 0.5; }
.kanban-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.8rem;
  color: var(--color-heading, #111827);
  min-width: 0;
}
.kanban-search-input::placeholder { color: #9ca3af; }
.kanban-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.7rem;
  color: #9ca3af;
  padding: 0 2px;
  flex-shrink: 0;
  line-height: 1;
}
.kanban-search-clear:hover { color: #374151; }
body.dark .kanban-search-wrap { background: #1e2530; border-color: #374151; }
body.dark .kanban-search-input { color: #f3f4f6; }
body.dark .kanban-search-clear:hover { color: #e5e7eb; }

.kanban-add-col-btn {
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.kanban-add-col-btn:hover { background: #4f46e5; }
.kanban-history-btn {
  background: var(--color-background-light, #e8eaed);
  color: var(--color-heading, #374151);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 7px;
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.kanban-history-btn:hover { background: rgba(99,102,241,0.1); color: #6366f1; }
.kanban-delete-board-btn {
  background: none;
  color: #ef4444;
  border: 1px solid rgba(239,68,68,0.3);
  border-radius: 7px;
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  line-height: 1;
}
.kanban-delete-board-btn:hover { background: rgba(239,68,68,0.08); }
body.dark .kanban-history-btn { background: #1e2530; border-color: #374151; color: #e5e7eb; }
body.dark .kanban-history-btn:hover { background: rgba(99,102,241,0.15); color: #a5b4fc; }
body.dark .kanban-delete-board-btn { border-color: rgba(239,68,68,0.4); }
.kanban-header h2 {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-heading, #111827);
  margin: 0;
}
.kanban-refresh-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: var(--color-body, #6b7280);
  padding: 2px 6px;
  border-radius: 4px;
}
.kanban-refresh-btn:hover { background: var(--color-background-light, #e5e7eb); }
.kanban-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
}
.kanban-empty--full {
  flex: 1;
  width: 100%;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
  align-items: center;
  justify-content: center;
}
.kanban-empty-icon { font-size: 2.5rem; opacity: 0.2; }
.kanban-empty-title { font-size: 1.05rem; font-weight: 600; color: var(--color-heading, #374151); margin: 0; }
.kanban-empty-sub { font-size: 0.85rem; color: #9ca3af; margin: 0; max-width: 320px; }
.kanban-empty-btn {
  margin-top: 0.5rem;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.kanban-empty-btn:hover { background: #4f46e5; }
body.dark .kanban-empty-title { color: #e5e7eb; }
.kanban-board {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  flex: 1;
  align-items: stretch;
  padding-bottom: 1rem;
}
.kanban-column {
  min-width: 290px;
  max-width: 290px;
  border-radius: 10px;
  padding: 0.6rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
  border: 1px solid rgba(0,0,0,0.06);
}
.column-header {
  border-top: 3px solid;
  border-radius: 4px 4px 0 0;
  padding: 0.5rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.column-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--color-heading, #374151);
}
.column-count {
  background: rgba(0,0,0,0.08);
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-heading, #374151);
}
.column-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 40px;
  overflow-y: auto;
  flex: 1;
  padding-right: 2px;
}
.column-cards::-webkit-scrollbar { width: 4px; }
.column-cards::-webkit-scrollbar-track { background: transparent; }
.column-cards::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 4px; }
body.dark .column-cards::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); }
.kanban-card {
  background: var(--white, #ffffff);
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  cursor: grab;
  border: 1px solid var(--color-border, #e5e7eb);
  transition: box-shadow 0.15s;
}
.kanban-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.kanban-card:active { cursor: grabbing; }
.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.card-contact { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.card-contact-name {
  font-weight: 600;
  font-size: 0.83rem;
  color: var(--color-heading, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-contact-phone {
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 1px;
}
body.dark .card-contact-phone { color: #9ca3af; }
.card-assignee-pill {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(99,102,241,0.1);
  border-radius: 20px;
  padding: 2px 7px 2px 3px;
  flex-shrink: 0;
  max-width: 130px;
}
.card-assignee-initials {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #6366f1;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  font-weight: 700;
  flex-shrink: 0;
}
.card-assignee-fullname {
  font-size: 0.7rem;
  font-weight: 500;
  color: #4f46e5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-last-message {
  font-size: 0.74rem;
  color: var(--color-body, #6b7280);
  margin: 0 0 0.35rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-last-message--unread {
  color: var(--color-heading, #111827);
  font-weight: 600;
}
body.dark .card-last-message--unread { color: #f3f4f6; }
.card-contact-name--unread { font-weight: 700; }
.card-contact-name-row {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.card-contact-name-row .card-contact-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-unread-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  max-width: 16px;
  padding: 0;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  font-size: 0.56rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  box-sizing: border-box;
}
.card-footer {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}
.card-assignee { margin-right: auto; }
.assignee-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
}
.assignee-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d1d5db;
  font-size: 0.58rem;
  font-weight: 700;
  color: #374151;
}
.waiting-badge {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
}
.waiting-badge--ok      { background: #dcfce7; color: #166534; }
.waiting-badge--warning { background: #fef9c3; color: #854d0e; }
.waiting-badge--danger  { background: #fee2e2; color: #991b1b; }
/* Drop indicator (ordenamiento manual) */
.kanban-card--drop-before { box-shadow: 0 -3px 0 0 #6366f1, 0 -6px 8px -4px rgba(99,102,241,0.5) !important; }
.kanban-card--drop-after  { box-shadow: 0 3px 0 0 #6366f1, 0 6px 8px -4px rgba(99,102,241,0.5) !important; }
/* SLA */
.kanban-card--sla {
  border: 1.5px solid #ef4444 !important;
  box-shadow: 0 0 0 1px rgba(239,68,68,0.15), 0 1px 4px rgba(239,68,68,0.2);
}
.sla-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid rgba(239,68,68,0.35);
  border-radius: 6px;
  padding: 1px 6px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}
body.dark .sla-badge { background: rgba(239,68,68,0.18); color: #fca5a5; border-color: rgba(239,68,68,0.4); }
.col-sla-row { display: flex; gap: 0.5rem; width: 100%; }
.col-modal-input.col-sla-input { flex: 1; width: auto; min-width: 0; font-size: 0.95rem; }
.col-modal-input.col-sla-input::-webkit-inner-spin-button { opacity: 1; }
.col-modal-input.col-sla-unit { width: 120px; flex: 0 0 120px; }
.col-sla-preview {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #b45309;
  background: rgba(245,158,11,0.1);
  border: 1px solid rgba(245,158,11,0.3);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  line-height: 1.35;
}
body.dark .col-sla-preview { color: #fcd34d; background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.35); }
.priority-badge {
  font-size: 0.63rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
}
.priority-badge--urgent { background: #fee2e2; color: #991b1b; }
.priority-badge--high   { background: #ffedd5; color: #9a3412; }
.priority-badge--medium { background: #fef9c3; color: #854d0e; }
.priority-badge--low    { background: #dbeafe; color: #1e40af; }
.card-labels { display: flex; flex-wrap: wrap; gap: 3px; align-items: center; }
.card-label {
  font-size: 0.63rem;
  background: #ede9fe;
  color: #5b21b6;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 500;
  transition: none;
}
.card-conv-fallback { font-size: 0.83rem; color: #6b7280; margin: 0; }

/* Acciones rápidas */
.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-bottom: 0.4rem;
  opacity: 0;
  transition: opacity 0.15s;
}
.kanban-card:hover .card-actions { opacity: 1; }
.card-action-btn {
  border: none;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 0.75rem;
  cursor: pointer;
  line-height: 1.4;
  font-weight: 600;
}
.card-action-btn--go {
  background: #ede9fe;
  color: #5b21b6;
}
.card-action-btn--go:hover { background: #ddd6fe; }
.card-action-btn--remove {
  background: #fee2e2;
  color: #991b1b;
}
.card-action-btn--remove:hover { background: #fecaca; }


/* Dark mode */
body.dark .kanban-container { background: #151a23; }
body.dark .column-name,
body.dark .column-count     { color: #e5e7eb; }
body.dark .column-count     { background: rgba(255,255,255,0.08); }
body.dark .kanban-column    { border-color: rgba(255,255,255,0.06); }
body.dark .kanban-card      { background: #242d3a; border-color: #374151; }
body.dark .kanban-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.35); }
body.dark .card-contact-name,
body.dark .kanban-header h2 { color: #f3f4f6; }
body.dark .card-last-message { color: #9ca3af; }
body.dark .card-conv-id     { color: #6b7280; }
body.dark .card-assignee-pill { background: rgba(99,102,241,0.18); }
/* Agente inline */
.card-agent-wrap { position: relative; flex-shrink: 0; }
.card-assignee-pill--clickable { cursor: pointer; transition: box-shadow 0.12s; }
.card-assignee-pill--clickable:hover { box-shadow: 0 0 0 2px rgba(99,102,241,0.35); }
.agent-add-btn {
  background: none;
  border: 1px dashed #9ca3af;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 0.68rem;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.12s;
}
.agent-add-btn:hover { border-color: #6366f1; color: #6366f1; }
body.dark .agent-add-btn { border-color: #4b5563; color: #9ca3af; }
body.dark .agent-add-btn:hover { border-color: #a5b4fc; color: #a5b4fc; }
.card-inline-menu--agent {
  top: calc(100% + 4px);
  bottom: auto;
  right: 0;
  left: auto;
  max-height: 220px;
  overflow-y: auto;
  min-width: 180px;
}
.agent-menu-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #6366f1;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  flex-shrink: 0;
}
body.dark .card-assignee-fullname { color: #a5b4fc; }
body.dark .kanban-refresh-btn { color: #9ca3af; }

/* Modal auditoría */
.audit-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.audit-modal {
  background: var(--white, #fff);
  border-radius: 12px;
  width: 420px;
  max-width: 95vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  overflow: hidden;
}
.audit-modal--wide {
  width: 580px;
}
.audit-contact-name {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 500;
  margin-left: 2px;
}
.audit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.9rem;
  color: #111827;
}
.audit-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 4px;
}
.audit-close:hover { background: #f3f4f6; }
.audit-loading, .audit-empty {
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.85rem;
}
.audit-timeline {
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.audit-entry {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}
.audit-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}
.dot--created { background: #22c55e; }
.dot--moved    { background: #6366f1; }
.dot--removed  { background: #ef4444; }
.audit-body { flex: 1; min-width: 0; }
.audit-action {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 2px;
}
.audit-action-label {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
}
.dot--created { background: #22c55e; }
.audit-action-label.dot--created { background: #dcfce7; color: #166534; }
.audit-action-label.dot--moved   { background: #ede9fe; color: #5b21b6; }
.audit-action-label.dot--removed { background: #fee2e2; color: #991b1b; }
.audit-columns { display: flex; align-items: center; gap: 0.3rem; font-size: 0.8rem; }
.audit-col-from { color: #9ca3af; }
.audit-arrow    { color: #9ca3af; font-size: 0.7rem; }
.audit-col-to   { font-weight: 600; }
.audit-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.72rem;
  color: #9ca3af;
}
.audit-user { font-weight: 500; color: #6b7280; }

/* Dark mode modal */
body.dark .audit-modal       { background: #1e2530; }
body.dark .audit-modal-header { color: #f3f4f6; border-color: #374151; }
body.dark .audit-close:hover  { background: #374151; }
body.dark .audit-close        { color: #9ca3af; }
body.dark .audit-col-from     { color: #6b7280; }
body.dark .audit-user         { color: #9ca3af; }
body.dark .card-action-btn--go    { background: rgba(99,102,241,0.2); color: #a5b4fc; }
body.dark .card-action-btn--remove { background: rgba(239,68,68,0.15); color: #fca5a5; }
body.dark .agent-name  { color: #9ca3af; }
body.dark .agent-initials { background: #374151; color: #e5e7eb; }

/* Column header actions */
.col-header-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.col-delete-btn, .col-move-btn, .col-sync-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 1px 5px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;
  line-height: 1.4;
}
.col-move-btn { font-size: 1rem; font-weight: 700; }
.col-delete-btn { font-size: 0.7rem; }
.col-sync-btn { font-size: 0.85rem; }
.kanban-column:hover .col-delete-btn,
.kanban-column:hover .col-move-btn,
.kanban-column:hover .col-sync-btn { opacity: 1; }
.col-delete-btn:hover { background: #fee2e2; color: #991b1b; }
.col-move-btn:hover { background: rgba(99,102,241,0.1); color: #6366f1; }
.col-sync-btn:hover { background: rgba(16,185,129,0.1); color: #059669; }
body.dark .col-delete-btn:hover { background: rgba(239,68,68,0.2); color: #fca5a5; }
body.dark .col-move-btn:hover { background: rgba(99,102,241,0.2); color: #a5b4fc; }
.col-inbox-badge { font-size: 0.8rem; }

.col-tag-badge {
  font-size: 0.6rem;
  background: rgba(99,102,241,0.12);
  color: #6366f1;
  padding: 1px 5px;
  border-radius: 8px;
  margin-left: 4px;
  vertical-align: middle;
  font-weight: 500;
}
.col-tag-badge--personal { background: rgba(107,114,128,0.12); color: #6b7280; }

/* Column create modal */
.col-modal { width: 400px; }
.col-modal-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 0.75rem; }
.col-modal-label { font-size: 0.78rem; font-weight: 600; color: #374151; margin-bottom: -0.4rem; }
body.dark .col-modal-label { color: #d1d5db; }
.col-modal-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  background: var(--white, #fff);
  color: #111827;
  box-sizing: border-box;
}
body.dark .col-modal-input { background: #1e2530; border-color: #374151; color: #f3f4f6; }
body.dark .col-modal-input option { background: #1e2530; color: #f3f4f6; }
.col-modal-input:focus { outline: none; border-color: #6366f1; }
.col-color-swatches { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.col-swatch {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  outline-offset: 2px;
  transition: transform 0.1s;
}
.col-swatch:hover { transform: scale(1.15); }
.col-modal-radios { display: flex; gap: 1rem; }
.col-modal-radios label { font-size: 0.82rem; color: #374151; display: flex; align-items: center; gap: 0.3rem; cursor: pointer; }
body.dark .col-modal-radios label { color: #d1d5db; }
.col-modal-label-wrap { display: flex; flex-direction: column; gap: 0.4rem; }
.col-modal-hint { font-size: 0.72rem; color: #9ca3af; margin: 0; }
.col-modal-error { font-size: 0.78rem; color: #dc2626; background: #fee2e2; padding: 0.4rem 0.75rem; border-radius: 6px; }
.col-modal-save {
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.25rem;
}
.col-modal-save:hover { background: #4f46e5; }
.col-modal-save:disabled { opacity: 0.6; cursor: not-allowed; }
.col-modal-checkbox-wrap {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  background: var(--color-background-light, #f9fafb);
}
.col-modal-checkbox-wrap input[type="checkbox"] { margin-top: 3px; flex-shrink: 0; cursor: pointer; accent-color: #6366f1; width: 15px; height: 15px; }
.col-modal-checkbox-wrap strong { font-size: 0.83rem; color: var(--color-heading, #111827); }
body.dark .col-modal-checkbox-wrap { background: #151a23; border-color: #374151; }
body.dark .col-modal-checkbox-wrap strong { color: #f3f4f6; }

/* Confirm delete modal */
.confirm-modal {
  width: 380px;
  padding: 2rem 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
}
.confirm-modal-icon { font-size: 2rem; line-height: 1; }
.confirm-modal-title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--color-heading, #111827); }
.confirm-modal-msg { margin: 0; font-size: 0.84rem; color: #6b7280; line-height: 1.55; }
.confirm-modal-msg strong { color: var(--color-heading, #111827); }
.confirm-modal-actions { display: flex; gap: 0.75rem; width: 100%; margin-top: 0.5rem; }
.confirm-btn {
  flex: 1;
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.confirm-btn--cancel { background: var(--color-background-light, #f3f4f6); color: #374151; }
.confirm-btn--cancel:hover { background: #e5e7eb; }
.confirm-btn--danger { background: #ef4444; color: #fff; }
.confirm-btn--danger:hover { background: #dc2626; }
.confirm-btn--danger:disabled { opacity: 0.6; cursor: not-allowed; }
body.dark .confirm-modal-title { color: #f3f4f6; }
body.dark .confirm-modal-msg strong { color: #f3f4f6; }
body.dark .confirm-btn--cancel { background: #374151; color: #e5e7eb; }
body.dark .confirm-btn--cancel:hover { background: #4b5563; }

/* compact filter button */
.kanban-filter-btn-wrap { position: relative; margin-bottom: 0.5rem; }
.kanban-filter-btn { display: inline-flex; align-items: center; border: 1px solid rgba(0,0,0,0.15); background: #fff; border-radius: 6px; padding: 4px 10px; font-size: 0.78rem; font-weight: 500; cursor: pointer; color: #374151; transition: all 0.12s; white-space: nowrap; }
.kanban-filter-btn:hover { border-color: #6366f1; color: #6366f1; }
.kanban-filter-btn--active { border-color: #6366f1; color: #6366f1; background: #eef2ff; }
.kanban-filter-badge { display: inline-flex; align-items: center; justify-content: center; background: #6366f1; color: #fff; border-radius: 10px; font-size: 0.65rem; font-weight: 700; min-width: 16px; height: 16px; padding: 0 4px; margin-left: 5px; }
.kanban-filter-dropdown { position: absolute; top: calc(100% + 4px); left: 0; z-index: 300; background: #fff; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.12); min-width: 180px; padding: 8px 0; }
.kfd-section { padding: 6px 12px; }
.kfd-label { font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; margin-bottom: 4px; }
.kfd-options { display: flex; flex-direction: column; gap: 2px; }
.kfd-option { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; color: #374151; padding: 3px 4px; border-radius: 4px; cursor: pointer; }
.kfd-option:hover { background: #f3f4f6; }
.kfd-option input[type="checkbox"] { cursor: pointer; accent-color: #6366f1; }
.kfd-footer { border-top: 1px solid rgba(0,0,0,0.08); padding: 6px 12px 2px; }
.kfd-clear { background: none; border: none; font-size: 0.75rem; color: #9ca3af; cursor: pointer; padding: 0; }
.kfd-clear:hover { color: #ef4444; }
body.dark .kanban-filter-btn { background: #1f2937; border-color: rgba(255,255,255,0.12); color: #d1d5db; }
body.dark .kanban-filter-btn--active { background: #312e81; border-color: #6366f1; color: #a5b4fc; }
body.dark .kanban-filter-dropdown { background: #1f2937; border-color: rgba(255,255,255,0.1); }
body.dark .kfd-option { color: #d1d5db; }
body.dark .kfd-option:hover { background: #374151; }

/* Inline priority dropdown */
.card-priority-wrap { position: relative; display: inline-flex; }
.priority-badge { cursor: pointer; user-select: none; }
.priority-badge:hover { opacity: 0.8; }
.priority-add-btn { font-size: 0.65rem; border: 1px dashed #d1d5db; background: none; border-radius: 10px; padding: 1px 7px; cursor: pointer; color: #9ca3af; white-space: nowrap; }
.priority-add-btn:hover { border-color: #6366f1; color: #6366f1; }
.card-inline-menu { position: absolute; bottom: calc(100% + 4px); left: 0; z-index: 300; background: var(--white, #fff); border: 1px solid var(--color-border, #e5e7eb); border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); min-width: 140px; overflow: hidden; }
.card-inline-menu-item { display: flex; align-items: center; gap: 0.4rem; padding: 7px 12px; font-size: 0.8rem; cursor: pointer; color: #374151; transition: background 0.1s; white-space: nowrap; }
.card-inline-menu-item:hover { background: #f3f4f6; }
.card-inline-menu-item--active { background: rgba(99,102,241,0.08); font-weight: 600; }
body.dark .card-inline-menu { background: #1e2530; border-color: #374151; }
body.dark .card-inline-menu-item { color: #e5e7eb; }
body.dark .card-inline-menu-item:hover { background: #2d3748; }

/* Inline label dropdown */
.card-label-add-btn { font-size: 0.62rem; border: 1px dashed #d1d5db; background: none; border-radius: 10px; padding: 1px 6px; cursor: pointer; color: #9ca3af; line-height: 1.4; }
.card-label-add-btn:hover { border-color: #6366f1; color: #6366f1; }
.card-label-wrap { position: relative; display: inline-flex; }
.label-menu { position: absolute; bottom: calc(100% + 4px); left: 0; z-index: 300; background: var(--white, #fff); border: 1px solid var(--color-border, #e5e7eb); border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); min-width: 160px; max-height: 200px; overflow-y: auto; }
.label-menu-item { display: flex; align-items: center; gap: 0.4rem; padding: 6px 12px; font-size: 0.78rem; cursor: pointer; color: #374151; transition: background 0.1s; }
.label-menu-item:hover { background: #f3f4f6; }
.label-menu-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.label-menu-check { margin-left: auto; font-size: 0.7rem; color: #6366f1; }
body.dark .label-menu { background: #1e2530; border-color: #374151; }
body.dark .label-menu-item { color: #e5e7eb; }
body.dark .label-menu-item:hover { background: #2d3748; }
</style>
